import { useState, useRef, useCallback, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Check, X } from "lucide-react"
import { redirectToPayFast } from "@/modules/subscriptions/infrastructure/payfast-redirect"
import Checkout from "@/modules/subscriptions/ui/components/Checkout"
import type { BillingInterval, BillingPlanDto } from "@/modules/subscriptions/dtos/billing-plan.dto"
import { createOrganization } from "@/modules/organizations/infrastructure/organization-service"
import { toast } from "sonner"
import { useGooglePlacesAutocomplete } from "@/modules/dashboard/ui/hooks/useGooglePlacesAutocomplete"
import type { OrganizationFormData } from "@/modules/organizations/entities/Organization"
import { useOrganizationsService } from "@/modules/organizations/hooks/use-organizations"
import { Currency } from "@/modules/organizations/enums/currency"
import { PlanType } from "@/modules/organizations/types/plan-type"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { validateCoupon } from "@/modules/coupon/infrastructure/coupon.api"
import type { CouponValidationDto } from "@/modules/coupon/dtos/coupon.dto"
import { createSubscription } from "@/modules/subscriptions/infrastructure/subscription.api"
import { setPendingPaymentReturn } from "@/lib/payment-return"
import { RegisterOrganizationWebsiteStep } from "./RegisterOrganizationWebsiteStep"
import { RegisterOrganizationDetailsStep } from "./RegisterOrganizationDetailsStep"
import {
  companyFormSchema,
  type CompanyFormValues,
} from "./registerOrganizationSchema"
import { RegisterOrganizationPlanStep } from "./RegisterOrganizationPlanStep"

const RegistrationStep = {
  WEBSITE: 1,
  DETAILS: 2,
  PLAN: 3,
  CHECKOUT: 4,
} as const
type RegistrationStep = (typeof RegistrationStep)[keyof typeof RegistrationStep]

export interface RegisterOrganizationModalProps {
  isOpen: boolean
  onClose: () => void
}

const mapBillingPlanToOrgPlan = (plan: BillingPlanDto): PlanType => {

  const code = plan.code?.toLowerCase()

  if (code === "recruitment") return PlanType.Recruitment
  if (code === "onboarding") return PlanType.Onboarding
  if (code === "protalent" || code === "fullhire") return PlanType.FullHiring

  const name = plan.name?.toLowerCase()
  if (name.includes("onboard")) return PlanType.Onboarding
  if (name.includes("recruit")) return PlanType.Recruitment

  return PlanType.FullHiring
}

export default function RegisterOrganizationModal({
  isOpen,
  onClose,
}: RegisterOrganizationModalProps) {
  const navigate = useNavigate()
  const { loadOrganizations, setOrganizationById } = useOrganizationsService()

  const [currentStep, setCurrentStep] = useState<RegistrationStep>(
    RegistrationStep.WEBSITE,
  )
  const [websiteUrl, setWebsiteUrl] = useState("")
  const [selectedPlan, setSelectedPlan] = useState<BillingPlanDto | null>(null)
  const [isYearly, setIsYearly] = useState(true)
  const [isProcessing, setIsProcessing] = useState(false)
  const [processingMessage, setProcessingMessage] = useState("Processing your request")
  const [appliedCoupon, setAppliedCoupon] =
    useState<CouponValidationDto | null>(null)

  const form = useForm<CompanyFormValues>({
    resolver: zodResolver(companyFormSchema),
    defaultValues: {
      companyName: "",
      streetAddress: "",
      city: "",
      state: "",
      zipCode: "",
      country: "",
      description: "",
      currency: Currency.ZAR,
    },
  })

  const addressSearchRef = useRef<HTMLInputElement>(null)

  const handleAddressSelect = useCallback(
    (address: {
      streetAddress: string
      city: string
      state: string
      zipCode: string
      country: string
    }) => {
      if (address.streetAddress)
        form.setValue("streetAddress", address.streetAddress)
      if (address.city) form.setValue("city", address.city)
      if (address.state) form.setValue("state", address.state)
      if (address.zipCode) form.setValue("zipCode", address.zipCode)
      if (address.country) form.setValue("country", address.country)
    },
    [form],
  )

  useGooglePlacesAutocomplete(
    addressSearchRef,
    handleAddressSelect,
    currentStep,
  )

  const handleCouponValidate = useCallback(
    async (code: string): Promise<CouponValidationDto> => {
      return validateCoupon(
        code,
        "00000000-0000-0000-0000-000000000000",
        selectedPlan?.id,
      )
    },
    [selectedPlan],
  )

  const handleDetailsLoaded = (details: OrganizationFormData) => {
    if (details.companyName) form.setValue("companyName", details.companyName)
    if (details.description) form.setValue("description", details.description)
    if (details.streetAddress) form.setValue("streetAddress", details.streetAddress)
    if (details.city) form.setValue("city", details.city)
    if (details.state) form.setValue("state", details.state)
    if (details.zipCode) form.setValue("zipCode", details.zipCode)
    if (details.country) form.setValue("country", details.country)
  }

  const handlePayment = async () => {
    if (!selectedPlan) return

    try {
      setIsProcessing(true)

      const values = form.getValues()
      const planChoice = mapBillingPlanToOrgPlan(selectedPlan)

      const orgPayload = {
        name: values.companyName,
        plan: planChoice,
        description: values.description,
        websiteUrl: websiteUrl || undefined,
        currency: values.currency,
        physicalAddress: {
          line1: values.streetAddress || "",
          city: values.city || "",
          state: values.state || "",
          country: values.country || "",
          postalCode: values.zipCode || "",
        },
      }

      const createOrgResult = await createOrganization(orgPayload)

      if (!createOrgResult.isSuccess) {
        if (createOrgResult.errorCode === "ORG_EXISTS")
          toast.error("An organisation with this name already exists")

        if (createOrgResult.errorCode !== "ORG_EXISTS")
          toast.error(
            "We could not create your organisation, please try again later.",
          )

        setIsProcessing(false)
        return
      }

      const createdOrg = createOrgResult.value
      const billingInterval: BillingInterval = isYearly ? "Annual" : "Monthly"

      const response = await createSubscription({
        planId: String(selectedPlan.id),
        billingInterval,
        organizationId: createdOrg.id,
        couponCode: appliedCoupon?.isValid
          ? appliedCoupon.couponCode
          : undefined,
      })

      if (response.trialActivated) {
        toast.success("Trial started! Taking you to your dashboard...")
        try {
          await loadOrganizations()
        } catch {
          // Non-fatal — org will still be selectable
        }
        setOrganizationById(createdOrg.id)
        navigate("/dashboard", { replace: true })
        return
      }

      setPendingPaymentReturn({ organizationId: createdOrg.id, returnPath: "/dashboard" })

      if (response.paymentRedirect) {
        setProcessingMessage("Redirecting to payment gateway")
        redirectToPayFast(
          response.paymentRedirect.redirectUrl,
          response.paymentRedirect.formData,
        )
      }
    } catch (error) {
      toast.error("Failed to create subscription")

      if (error instanceof Error) {
        if (error.message && error.message.includes("400")) {
          toast.error("Invalid subscription details. Please try again.")
        } else if (error.message && error.message.includes("404")) {
          toast.error("Selected plan not found. Please choose another plan.")
        }
      }

      setIsProcessing(false)
    }
  }

  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !isProcessing) onClose()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [isOpen, isProcessing, onClose])

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-50"
      onClick={(e) => {
        if (e.target === e.currentTarget && !isProcessing) onClose()
      }}
    >
      <div className="bg-card rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-y-auto animate-modal-pop">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold">
            {currentStep === RegistrationStep.WEBSITE && "Company Website"}
            {currentStep === RegistrationStep.DETAILS && "Company Details"}
            {currentStep === RegistrationStep.PLAN && "Choose A Pricing Plan"}
            {currentStep === RegistrationStep.CHECKOUT && "Review & Complete"}
          </h2>
          <Link
            onClick={isProcessing ? undefined : onClose}
            className={`p-1 rounded-md transition-colors ${isProcessing ? "pointer-events-none text-muted-foreground/40" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}
            aria-label="Close"
            to={""}
          >
            <X size={20} />
          </Link>
        </div>

        <div className="flex items-center justify-center p-4 bg-muted border-b">
          <div className="flex items-center space-x-2">
            {[
              { step: RegistrationStep.WEBSITE, label: "Website" },
              { step: RegistrationStep.DETAILS, label: "Details" },
              { step: RegistrationStep.PLAN, label: "Plan" },
              { step: RegistrationStep.CHECKOUT, label: "Checkout" },
            ].map(({ step, label }, index, arr) => (
              <div key={step} className="flex items-center">
                <div className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${currentStep >= step
                      ? "bg-primary text-primary-foreground"
                      : "bg-border text-muted-foreground"
                      }`}
                  >
                    {currentStep > step ? <Check size={14} /> : index + 1}
                  </div>
                  <span
                    className={`ml-2 text-sm font-medium ${currentStep >= step
                      ? index === 0
                        ? "text-foreground"
                        : "text-primary"
                      : "text-muted-foreground"
                      }`}
                  >
                    {label}
                  </span>
                </div>
                {index < arr.length - 1 && (
                  <div className="w-8 h-0.5 bg-border ml-2"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="p-6">
          {currentStep === RegistrationStep.WEBSITE && (
            <RegisterOrganizationWebsiteStep
              websiteUrl={websiteUrl}
              onWebsiteUrlChange={setWebsiteUrl}
              onDetailsLoaded={handleDetailsLoaded}
              onNext={() => setCurrentStep(RegistrationStep.DETAILS)}
              onCancel={onClose}
            />
          )}

          {currentStep === RegistrationStep.DETAILS && (
            <RegisterOrganizationDetailsStep
              form={form}
              addressSearchRef={addressSearchRef}
              onNext={() => setCurrentStep(RegistrationStep.PLAN)}
              onBack={() => setCurrentStep(RegistrationStep.WEBSITE)}
            />
          )}

          {currentStep === RegistrationStep.PLAN && (
            <RegisterOrganizationPlanStep
              onPlanSelect={(plan, yearly) => {
                setSelectedPlan(plan)
                setIsYearly(yearly)
                setAppliedCoupon(null)
                setCurrentStep(RegistrationStep.CHECKOUT)
              }}
              onBack={() => setCurrentStep(RegistrationStep.DETAILS)}
              isProcessing={isProcessing}
            />
          )}

          {currentStep === RegistrationStep.CHECKOUT && selectedPlan && (
            <Checkout
              companyForm={{
                companyName: form.getValues("companyName"),
                streetAddress: form.getValues("streetAddress"),
                city: form.getValues("city"),
                state: form.getValues("state"),
                zipCode: form.getValues("zipCode"),
                country: form.getValues("country"),
                description: form.getValues("description"),
              }}
              selectedPlan={selectedPlan}
              isYearly={isYearly}
              onBack={() => setCurrentStep(RegistrationStep.PLAN)}
              onComplete={handlePayment}
              isProcessing={isProcessing}
              processingMessage={processingMessage}
              appliedCoupon={appliedCoupon}
              onCouponChange={setAppliedCoupon}
              onCouponValidate={handleCouponValidate}
            />
          )}
        </div>
      </div>
    </div>
  )
}
